import { useTranslation } from 'react-i18next';
import i18next, { TOptions } from 'i18next';
import { useCallback } from 'react';
import { outcomeRegexArray } from '../localization/constants';
import { DEFAULT_LANGUAGE, outcomeCache } from '../localization/utils';
import { useMarketplaceConfig } from '../queries/marketplace';
import { getMarketTranslationStrategy } from '../localization/marketTranslation';
import { tError } from '../helpers/translation';
import { TMessage } from '../@types';

export type LabelledObject = {
  label: string;
  [key: string]: unknown;
};

const useLocalization = () => {
  const { t, ...useTranslationContext } = useTranslation();
  const { data: marketplaceConfig } = useMarketplaceConfig();

  const getLocalizedMessageParticipant = useCallback(
    (participant: string) => {
      if (participant === 'Administrator') {
        return t('messageAdministrator', { defaultValue: participant });
      }

      if (participant === 'CASINO') {
        return t('casino');
      }

      return participant;
    },
    [t]
  );

  const getLocalizedMessageTopic = useCallback(
    (message: Pick<TMessage, 'topic' | 'translationData'>) => {
      if (message.topic === 'Casino Jackpot') {
        return t('casinoJackpotTitle', { ns: 'common', defaultValue: message.topic });
      }

      const data = message.translationData;
      if (!data?.code) {
        return message.topic;
      }

      return t(`${data.code}Title`, {
        ...data.params,
        ns: 'common',
        defaultValue: message.topic,
      });
    },
    [t]
  );

  const getLocalizedConfig = useCallback(
    <T extends LabelledObject>(config: T[], options?: TOptions) => {
      return config.map((tab) => ({
        ...tab,
        label: t(tab.label, options) ?? '',
      }));
    },
    [t]
  );

  const getLocalizedOutcomeName = useCallback(
    (outcome: string) => {
      const language = i18next.language;
      const shouldLocalize = language !== DEFAULT_LANGUAGE;
      if (/^\d+-\d+$|^\d+(\.\d+)?$/.test(outcome) || !shouldLocalize) {
        return outcome;
      }
      const cacheKey = `${outcome}-${language}`;

      const cachedOutcomeValue = outcomeCache.get(cacheKey);

      if (cachedOutcomeValue !== undefined) {
        return cachedOutcomeValue;
      }

      let processed = outcome;

      outcomeRegexArray.forEach(({ rule, key }) => {
        processed = processed.replace(rule, t(key, key, { ns: 'outcomes' }));
      });

      outcomeCache.set(cacheKey, processed);

      return processed;
    },
    [t]
  );

  const getLocalizedMarketName = useCallback(
    (marketName: string, marketType?: string) => {
      const feedSource = marketplaceConfig?.sportsbookConfig?.feedSource;
      const strategy = getMarketTranslationStrategy(feedSource);

      return strategy(marketName?.trim(), marketType, t);
    },
    [t, marketplaceConfig]
  );

  const getLocalizedEventPart = useCallback(
    (eventPartName: string) => {
      return t(eventPartName, eventPartName, { ns: 'eventParts' });
    },
    [t]
  );

  const getLocalizedErrorMessage = useCallback(
    (errorCode: string, params?: Record<string, string | number>) => {
      const processedParams = { ...params };

      const entityLocalizersMap: Record<string, (value: string) => string> = {
        outcomeTypeName: getLocalizedOutcomeName,
        marketTypeName: getLocalizedMarketName,
      };

      for (const key in entityLocalizersMap) {
        const value = processedParams[key];

        if (typeof value === 'string') {
          processedParams[key] = entityLocalizersMap[key](value);
        }
      }

      return tError(errorCode, processedParams);
    },
    [getLocalizedOutcomeName, getLocalizedMarketName]
  );

  return {
    t,
    getLocalizedConfig,
    getLocalizedMessageTopic,
    getLocalizedMessageParticipant,
    getLocalizedOutcomeName,
    getLocalizedMarketName,
    getLocalizedEventPart,
    getLocalizedErrorMessage,
    ...useTranslationContext,
  };
};

export default useLocalization;
